import React, { useEffect, useRef, useState, useCallback } from 'react';

import { processTextWithLinks } from '../../utilities/processTextWithLinks';
import { setupShadowDOMOverride } from '../../utilities/setupShadowDOMOverride';
import styles from './google-maps-location-picker.module.css';

/**
 * Loads the Google Maps Places API (New) script dynamically.
 * @returns {Promise<void>} Resolves when the script is loaded.
 */

// Get API key from environment variable (replaced at build time by Vite).
// Note: This must be in a .env file in the project root and the dev server must be restarted.
const apiKey = import.meta.env.VITE_STARMAP_API_GOOGLE_PLACES_KEY;

// @ts-expect-error - this is a global function that is used to initialize the Places API.
window.initPlaces = () => {
  document.dispatchEvent(new CustomEvent('places-api-initialized'));
};

/**
 * Check if the Google Maps Places API is already loaded.
 * @returns {boolean}
 */
const isPlacesApiReady = () => {
  // @ts-expect-error - Google Maps API types not available
  return !!(window.google?.maps?.places?.PlaceAutocompleteElement);
};

const loadGoogleMapsScript = () => {
  return new Promise((/** @type {(value?: void) => void} */ resolve, reject) => {
    // Check if Google Maps API is already loaded with Places library.
    // @ts-expect-error - Google Maps API types not available
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    // Check if script is already being loaded.
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    if (!apiKey) {
      reject(
        new Error(
          'Google Maps API key not found. Please create a .env file in the project root with VITE_STARMAP_API_GOOGLE_PLACES_KEY=your_key_here and restart the dev server.'
        )
      );
      return;
    }

    // Load the Google Maps Places API (New) script with places library.
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=initPlaces`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
};

/**
 * Helper function to get internal input from PlaceAutocompleteElement.
 * @param {any} element
 * @returns {HTMLInputElement | null}
 */
const getInternalInput = (element) => {
  if (element?.shadowRoot) {
    return element.shadowRoot.querySelector('input');
  }
  return element?.querySelector('input') || null;
};

/**
 * Helper function to extract serializable place data.
 * @param {any} place
 * @returns {Object}
 */
const extractPlaceData = (place) => {
  try {
    return {
      displayName: place.displayName || null,
      formattedAddress: place.formattedAddress || null,
      location: place.location
        ? {
            lat: typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat,
            lng: typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng
          }
        : null,
      addressComponents: place.addressComponents
        ? place.addressComponents
            .map((comp) => ({
              longText: comp.longText || null,
              shortText: comp.shortText || null,
              types: comp.types || []
            }))
            .filter((comp) => comp !== null)
        : null,
      id: place.id || null
    };
  } catch (error) {
    console.error('Error extracting place data:', error);
    return {
      formattedAddress: place.formattedAddress || null
    };
  }
};

/**
 * GoogleMapsLocationPicker component displays a google maps location picker input with autocomplete functionality using Places API (New).
 *
 * @param {Object} props - The component props
 * @param {string} props.id - The input id
 * @param {string} props.name - The input name
 * @param {boolean} props.required - Whether the input is required
 * @param {string} props.labelText - The label text
 * @param {string} props.locationPickerHeading - The location picker heading
 * @param {function} props.onChange - Callback when location selection changes
 * @param {(state: { isUnavailable: boolean, message: string }) => void} [props.onAvailabilityChange] - Callback for external unavailable-state messaging
 * @param {Object} props.autocompleteOptions - Options to pass to PlaceAutocompleteElement (requestOptions, componentRestrictions, etc.)
 * @param {string} props.defaultValue - Default value to display in the input
 * @returns {import('react').JSX.Element} The rendered component
 */
export const GoogleMapsLocationPicker = ({
  id,
  name,
  required = false,
  labelText,
  locationPickerHeading,
  onChange,
  onAvailabilityChange,
  autocompleteOptions = {},
  defaultValue = ''
}) => {
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const autocompleteElementRef = useRef(/** @type {any} */ (null));
  const onChangeRef = useRef(onChange);
  const [isAutocompleteActive, setIsAutocompleteActive] = useState(false);
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);
  const [serviceUnavailableMessage, setServiceUnavailableMessage] = useState('');

  // Keep onChange ref in sync without causing re-renders.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (onAvailabilityChange) {
      onAvailabilityChange({
        isUnavailable: isServiceUnavailable,
        message: serviceUnavailableMessage
      });
    }
  }, [isServiceUnavailable, serviceUnavailableMessage, onAvailabilityChange]);

  /**
   * Creates a fresh PlaceAutocompleteElement, inserts it into the DOM,
   * wires up event listeners, and sets refs/state.
   */
  const createAutocompleteElement = useCallback(() => {
    if (!containerRef.current || !inputRef.current) return;

    // @ts-expect-error - Google Maps API types not available
    const PlaceAutocompleteElement = window.google?.maps?.places?.PlaceAutocompleteElement;
    if (!PlaceAutocompleteElement) return;

    const container = containerRef.current;

    // Remove any existing PlaceAutocompleteElement before inserting a new one.
    const existingElements = container.querySelectorAll('gmp-place-autocomplete');
    existingElements.forEach((el) => el.remove());

    // Create element.
    const element = new PlaceAutocompleteElement();

    // Configure request options.
    element.request = {
      types: autocompleteOptions.types || autocompleteOptions.requestOptions?.types || ['geocode'],
      ...(autocompleteOptions.componentRestrictions && {
        componentRestrictions: autocompleteOptions.componentRestrictions
      }),
      ...(autocompleteOptions.requestOptions && autocompleteOptions.requestOptions)
    };

    element.setAttribute('id', `${id}-autocomplete`);
    element.style.width = '100%';
    element.style.display = 'block';
    element.className = 'field__input ' + styles['google-maps-location-picker__input'];

    // Insert into DOM after the hidden input.
    if (inputRef.current?.parentNode) {
      const parent = inputRef.current.parentNode;
      if (inputRef.current.nextSibling) {
        parent.insertBefore(element, inputRef.current.nextSibling);
      } else {
        parent.appendChild(element);
      }
    }

    autocompleteElementRef.current = element;
    setIsAutocompleteActive(true);
    setIsServiceUnavailable(false);
    setServiceUnavailableMessage('');

    // Wait for shadow DOM to initialise, then configure internal input.
    setTimeout(() => {
      const internalInput = getInternalInput(element);
      if (internalInput && inputRef.current) {
        internalInput.className = inputRef.current.className;
        internalInput.id = inputRef.current.id || `${id}-internal`;
        internalInput.name = name || '';
        inputRef.current.name = name || '';
        internalInput.required = required;

        // Set default value.
        if (defaultValue !== undefined) {
          internalInput.value = defaultValue || '';
          inputRef.current.value = defaultValue || '';
        }

        // Keep hidden input in sync.
        internalInput.addEventListener('input', () => {
          if (inputRef.current && internalInput.value) {
            inputRef.current.value = internalInput.value;
          }
        });
      }
    }, 150);

    // Listen for place selection.
    element.addEventListener('gmp-select', async (event) => {
      try {
        const place = event.placePrediction.toPlace();
        await place
          .fetchFields({
            fields: ['displayName', 'formattedAddress', 'location', 'addressComponents', 'id']
          })
          .catch((error) => {
            console.warn('Error fetching place fields:', error);
          });

        const address = place.formattedAddress || '';
        const locationPlaceData = extractPlaceData(place);
        const currentInternalInput = getInternalInput(element);

        // Store in hidden input.
        if (inputRef.current) {
          inputRef.current.value = address;
          inputRef.current.setAttribute('data-location-place', JSON.stringify(locationPlaceData));
        }

        // Update visible input.
        if (currentInternalInput) {
          currentInternalInput.value = address;
          setTimeout(() => {
            if (currentInternalInput && currentInternalInput.value !== address) {
              currentInternalInput.value = address;
            }
          }, 100);
        }

        if (onChangeRef.current) {
          onChangeRef.current(address, locationPlaceData);
        }
      } catch (error) {
        console.error('Error handling place selection:', error);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, name, defaultValue]);

  /**
   * Removes the current PlaceAutocompleteElement from the DOM and resets refs/state.
   */
  const destroyAutocompleteElement = useCallback(() => {
    const element = autocompleteElementRef.current;
    if (element && element.parentNode) {
      element.parentNode.removeChild(element);
    }
    autocompleteElementRef.current = null;
    setIsAutocompleteActive(false);

    // Also remove any stray elements from the container.
    if (containerRef.current) {
      const remaining = containerRef.current.querySelectorAll('gmp-place-autocomplete');
      remaining.forEach((el) => el.remove());
    }
  }, []);

  // Main initialisation effect.
  useEffect(() => {
    const cleanupShadowOverride = setupShadowDOMOverride();

    /**
     * Attempt to initialise (or re-initialise) the autocomplete element.
     * Called both from the one-time event listener and directly when the API is already loaded.
     */
    const tryInit = () => {
      if (!isPlacesApiReady()) return;

      // If an element already exists AND is still attached to the DOM, just update attributes.
      if (autocompleteElementRef.current) {
        const internalInput = getInternalInput(autocompleteElementRef.current);
        if (internalInput && autocompleteElementRef.current.parentNode) {
          // Element is functional — update name/value and bail.
          if (inputRef.current) {
            internalInput.name = name || '';
            inputRef.current.name = name || '';
          }
          return;
        }
        // Element is stale/disconnected — destroy it so we can recreate below.
        destroyAutocompleteElement();
      }

      // Create a fresh element.
      createAutocompleteElement();
    };

    // Listen for initial script load event.
    const onApiReady = () => tryInit();
    document.addEventListener('places-api-initialized', onApiReady);

    // Kick off script load (no-op if already loaded).
    const fallbackTimer = setTimeout(() => {
      if (!isPlacesApiReady()) {
        setIsServiceUnavailable(true);
        setIsAutocompleteActive(false);
        setServiceUnavailableMessage(
          'Location search is currently unavailable. Please try again in a moment.'
        );
      }
    }, 5000);

    loadGoogleMapsScript()
      .then(() => {
        if (isPlacesApiReady()) {
          setIsServiceUnavailable(false);
          setServiceUnavailableMessage('');
          tryInit();
        }
      })
      .catch((error) => {
        console.warn('Google Places API unavailable:', error);
        setIsServiceUnavailable(true);
        setIsAutocompleteActive(false);
        setServiceUnavailableMessage(
          'Location search is currently unavailable. Please try again in a moment.'
        );
      });

    // If the API is already available (HMR, navigating back, reset), init immediately.
    if (isPlacesApiReady()) {
      setIsServiceUnavailable(false);
      setServiceUnavailableMessage('');
      tryInit();
    }

    // Cleanup — always returned so event listeners never leak.
    return () => {
      cleanupShadowOverride();
      clearTimeout(fallbackTimer);
      document.removeEventListener('places-api-initialized', onApiReady);
      destroyAutocompleteElement();
    };
  }, [id, name, createAutocompleteElement, destroyAutocompleteElement]);

  // Keep defaultValue in sync (important for reset clearing the field).
  useEffect(() => {
    if (autocompleteElementRef.current) {
      const internalInput = getInternalInput(autocompleteElementRef.current);
      if (internalInput && defaultValue !== undefined && internalInput.value !== defaultValue) {
        internalInput.value = defaultValue || '';
        if (inputRef.current) {
          inputRef.current.value = defaultValue || '';
        }
      }
    }
  }, [defaultValue]);

  return (
    <div ref={containerRef} className={styles['google-maps-location-picker'] + ' field'}>
      {!isServiceUnavailable && (
        <>
          <label className={'field__label'} htmlFor={id}>
            {processTextWithLinks(labelText)}
          </label>
          {/* Hidden input for form submission - will be replaced by PlaceAutocompleteElement */}
          <input
            ref={inputRef}
            className={`${styles['google-maps-location-picker__input']} ${isAutocompleteActive ? styles['google-maps-location-picker__input--hidden'] : ''} field__input`}
            id={id}
            name={name}
            type="text"
            required={required}
            autoComplete="off"
          />
          <div className={styles['google-maps-location-picker__label']}>
            <p className={'body-xs'}>{processTextWithLinks(locationPickerHeading)}</p>
          </div>
        </>
      )}
    </div>
  );
};
